import { ExternalLinkIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import { FormattedDate } from 'components/misc/FormattedDate';
import { RecordChip } from 'components/record-chip/RecordChip';
import { Button } from 'components/ui/button';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { InvoiceStatus } from 'pages/invoices/InvoiceTableCells';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { InvoiceData, InvoiceDataListItem } from 'types/invoices';
import { t } from 'utils/getTranslations';
import { ReadCard } from './GeneralCard';

const HEAD_CLASS = 'py-2 text-start font-medium';

/**
 * Previous invoices of the new design: the case's earlier invoices as a plain table (each number its chip, opening in
 * a new tab), then "View unsent invoices" for the client with what it lists beside it.
 */
export function PreviousInvoicesSection({
    invoice,
    classifiers,
}: {
    invoice: InvoiceData;
    classifiers: Record<string, ClassifierData>;
}) {
    const previousInvoices = invoice.previousInvoices ?? [];
    return (
        <ReadCard
            title={
                <span className="flex items-center gap-1.5">
                    <FormattedMessage id="invoices.general-tab.previous-invoices.title" />
                    <InfoTooltip text="invoices.general-tab.previous-invoices.tooltips.title" />
                </span>
            }
        >
            <div className="flex flex-col gap-3">
                {previousInvoices.length > 0 ? (
                    <div className="max-h-[325px] overflow-y-auto">
                        <table className="text-meta w-full border-collapse">
                            <thead className="bg-card sticky top-0">
                                <tr className="border-divider text-foreground-tertiary border-b">
                                    <th scope="col" className={`${HEAD_CLASS} pe-2`}>
                                        <FormattedMessage id="invoices.invoice" />
                                    </th>
                                    <th scope="col" className={`${HEAD_CLASS} px-2`}>
                                        <FormattedMessage id="invoices.invoice-date" />
                                    </th>
                                    <th scope="col" className={`${HEAD_CLASS} px-2`}>
                                        <FormattedMessage id="invoices.status" />
                                    </th>
                                    <th scope="col" className="py-2 ps-2 text-end font-medium">
                                        <FormattedMessage id="invoices.total-amount" />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {previousInvoices.map((previous) => (
                                    <tr key={previous.id} className="border-divider border-b">
                                        <td className="py-2.5 pe-2">
                                            <RecordChip
                                                entityType="INVOICE"
                                                id={previous.id}
                                                label={previous.invoiceNumber ?? ''}
                                                target="_blank"
                                                hitArea={false}
                                            />
                                        </td>
                                        <td className="px-2 py-2.5 tabular-nums">
                                            {previous.invoiceDate ? (
                                                <FormattedDate value={previous.invoiceDate} />
                                            ) : null}
                                        </td>
                                        <td className="px-2 py-2.5">
                                            <InvoiceStatus
                                                invoice={previous as unknown as InvoiceDataListItem}
                                                classifiers={classifiers}
                                            />
                                        </td>
                                        <td className="py-2.5 ps-2 text-end tabular-nums">
                                            <MoneyDisplay
                                                amount={previous.totalAmountInPaymentCurrency}
                                                currency={previous.paymentCurrencyTypeCode}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-meta text-muted-foreground m-0">
                        <FormattedMessage id="invoices.no-invoices-found" />
                    </p>
                )}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <Button
                        variant="outline"
                        size="sm"
                        nativeButton={false}
                        render={
                            <a
                                href={`/invoices?mf=invoiceMatters.matter.clientParty.id=in=(${invoice.clientParty.id})&qf=invoiceStatusTypeCode=in=(DRAFT,REVIEWED,APPROVED,FINALIZED)`}
                                target="_blank"
                                rel="noopener noreferrer"
                            />
                        }
                    >
                        <FormattedMessage id="invoices.general-tab.previous-invoices.view-unsent-invoices" />
                        <ExternalLinkIcon data-icon="inline-end" aria-hidden className="size-3.5" />
                    </Button>
                    <span className="text-caption text-foreground-tertiary">
                        {t('invoices.general-tab.previous-invoices.tooltips.view-unsent-invoices')}
                    </span>
                </div>
            </div>
        </ReadCard>
    );
}
